import React, { FC, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  setMapStateData,
  setUserCurrPosData,
  setUserMapActivity,
  TUserMapActivity,
} from "../../api/store/commonSlice";
import { LegacyOrganizationAccount } from "../../api/types/Account";
import svgIcons from "../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../constants/constants";
import { routeUrls } from "../../navigation/routeUrls";
import { TGeozoneMapData, TLatLng, TMapState, TMapType } from "../../types/map";
import { useMapScriptLoadedHandler } from "./onMapScriptLoaded";
import { localStorageKeys } from "../../utils/localStorageItems";
import { useMapInit } from "../../hooks/mapInit";
import { useLoggedInUserData } from "../../utils/user";
import { useDebouncedCallback } from "use-debounce";
import { useGMapMode } from "../../hooks/mapGoogle";
import { fetchWeatherTimeslices, buildWeatherTileUrl } from "../../utils/weatherTiles";

type TBasicMapProps = {
  className?: string;
  mapRef?: any;
  mapData?: TGeozoneMapData;
  recordActivityAsPage?: "fleetMap" | "assetMap" | "heatMap";
  setMapData?: React.Dispatch<React.SetStateAction<TGeozoneMapData>>;
  onMapReady?: () => void;
  // explicit opt-in for the weather radar overlay - only the fleet/heat map
  // pages expose the toggle, so this must be passed rather than inferred
  // from recordActivityAsPage (which has its own, unrelated semantics and
  // isn't set consistently by every page that should support weather)
  enableWeather?: boolean;
  // show Google's native zoom + pan/recenter cluster in the bottom-right
  nativeMapControls?: boolean;
};

type TMapLoadingAnimationProps = {
  bgClassName?: string;
  bgOpacityClassName?: string;
  imageClassName?: string;
};

export const MapLoadingAnimation: FC<TMapLoadingAnimationProps> = ({
  bgClassName = "bg-white",
  bgOpacityClassName = "bg-opacity-90",
  imageClassName = "w-12 h-12",
}) => (
  <div
    className={`absolute inset-0 flex items-center justify-center ${bgClassName} ${bgOpacityClassName} z-map-loading-overlay`}
  >
    <img
      src={svgIcons.LoadingAnimation}
      alt="Loading animation"
      className={imageClassName}
    />
  </div>
);

export const getDefaultMapLayerOptions = (mapState: TMapState) => {
  const thisUser = {
    ownerOrganization: JSON?.parse?.(
      localStorage?.getItem(localStorageKeys.ownerOrganization) || "{}"
    ) as LegacyOrganizationAccount,
  };
  const clusteringEnabled =
    `${((thisUser.ownerOrganization || {}) as LegacyOrganizationAccount)?.enableclustering}` ||
    "0";
  return {
    traffic: mapState?.mapLayerOptions?.traffic ?? false,
    weather: mapState?.mapLayerOptions?.weather ?? false,
    three_d_building: mapState?.mapLayerOptions?.three_d_building ?? false,
    clustering: ["0", "2"].includes(clusteringEnabled) // disable clustering even for value 2
      ? false
      : mapState?.mapLayerOptions?.clustering || clusteringEnabled === "1", // adding the ` || clusteringEnabled === "1"` part picks the clustering value if not set
    hide_geozones: mapState?.mapLayerOptions?.hide_geozones ?? false, // 10/30/2024 changed default to false per client request
  };
};

const debugComponent = false;

const BasicMap: FC<TBasicMapProps> = React.memo(
  ({
    className = "",
    mapRef,
    mapData,
    recordActivityAsPage,
    nativeMapControls,
    setMapData, // deprecated
    onMapReady,
    enableWeather,
  }) => {
    const initRef = React.useRef(false);

    const userMapDefault = useLoggedInUserData("mapDefault");

    // Only log when userMapDefault changes — avoid logging on every render
    // disabled useEffect as this will help us check if re-render is occurring
    // React.useEffect(() => {
    if (APP_CONFIG.DEBUG.MAPS && !!debugComponent)
      console.log("userMapDefault in basicmap.tsx", userMapDefault);
    // }, [userMapDefault]);

    const getUserPreferredMapType = (): TMapType => {
      if (!userMapDefault) {
        return APP_CONFIG.MAPS.DEFAULTS.TYPE as TMapType;
      }

      const preference = parseInt(userMapDefault);

      switch (preference) {
        case 0:
          return "roadmap" as TMapType;
        case 1:
          return "satellite" as TMapType;
        default:
          return APP_CONFIG.MAPS.DEFAULTS.TYPE as TMapType;
      }
    };

    const userCurrPos: TLatLng = useSelector(
      (state: any) => state.commonReducer.userCurrPos
    );
    const mapState = useSelector(
      (state: { commonReducer: { mapState: TMapState } }) =>
        state.commonReducer.mapState
    );
    const userMapActivity = useSelector(
      (state: { commonReducer: { userMapActivity: TUserMapActivity } }) =>
        state.commonReducer.userMapActivity
    );
    const userMapRefreshing = useSelector(
      (state: { commonReducer: { userMapRefreshing: boolean } }) =>
        state.commonReducer.userMapRefreshing
    );

    // create refs that always contain LATEST values
    const activityRef = React.useRef<TUserMapActivity>(userMapActivity);
    activityRef.current = userMapActivity;
    const refreshingRef = React.useRef(false);
    refreshingRef.current = userMapRefreshing;

    const { pathname } = useLocation();
    const dispatch = useDispatch();

    const { gIdentifyAndSetMapModeToStore, gUpdateMapZoomLevelToStore } =
      useMapInit();

    const { onMapScriptLoaded } = useMapScriptLoadedHandler();

    const { gMapGetMode } = useGMapMode();

    const [loadingMap, setLoadingMap] = React.useState(true);
    // local (per-mount) readiness flag - unlike mapState.pageMapLoaded (global
    // redux state that never resets), this always starts false on every fresh
    // mount, so effects that need to react to "this map instance just became
    // ready" have a real rising edge to catch, even after navigate-away/back
    const [mapReady, setMapReady] = React.useState(false);

    const weatherAnimRef = React.useRef<{
      intervalId: ReturnType<typeof setInterval> | null;
      frame: number;
      layers: any[];
    }>({ intervalId: null, frame: 0, layers: [] });

    const clearWeatherAnimation = React.useCallback(() => {
      if (weatherAnimRef.current.intervalId !== null) {
        clearInterval(weatherAnimRef.current.intervalId);
        weatherAnimRef.current.intervalId = null;
      }
      if (mapRef?.current?.map) {
        const overlays = mapRef.current.map.overlayMapTypes;
        for (let i = overlays.getLength() - 1; i >= 0; i--) {
          if (overlays.getAt(i)?.name?.startsWith("Weather")) overlays.removeAt(i);
        }
      }
      weatherAnimRef.current.layers = [];
      weatherAnimRef.current.frame = 0;
    }, []);

    React.useEffect(() => {
      // weather is only offered on pages that explicitly opt in - other
      // BasicMap consumers (geozone form, camera admin widgets, etc.) share
      // the same global mapState, so without this guard a weather toggle
      // left on would leak the animated overlay onto every other map in the app
      if (!enableWeather || !mapRef?.current?.map || !mapReady) {
        clearWeatherAnimation();
        return;
      }
      const google = (window as any).google;
      if (!google) return;

      clearWeatherAnimation();
      if (!mapState?.mapLayerOptions?.weather) return;

      let cancelled = false;

      fetchWeatherTimeslices().then((timestamps) => {
        if (cancelled || !timestamps.length || !mapRef?.current?.map) return;

        const layers = timestamps.map((ts, i) =>
          new google.maps.ImageMapType({
            getTileUrl: (coord: any, zoom: any) =>
              buildWeatherTileUrl(ts, coord, zoom),
            tileSize: new google.maps.Size(256, 256),
            opacity: 0.7,
            name: `Weather_${i}`,
          })
        );

        weatherAnimRef.current.layers = layers;
        mapRef.current.map.overlayMapTypes.push(layers[0]);

        weatherAnimRef.current.intervalId = setInterval(() => {
          if (!mapRef?.current?.map) return;
          const { layers, frame } = weatherAnimRef.current;
          const overlays = mapRef.current.map.overlayMapTypes;
          for (let i = overlays.getLength() - 1; i >= 0; i--) {
            if (overlays.getAt(i)?.name?.startsWith("Weather")) overlays.removeAt(i);
          }
          const next = (frame + 1) % layers.length;
          weatherAnimRef.current.frame = next;
          overlays.push(layers[next]);
        }, 5000);
      });

      return () => {
        cancelled = true;
        clearWeatherAnimation();
      };
    }, [mapState?.mapLayerOptions?.weather, mapReady, enableWeather]);

    const debouncedSetUserMapActivity = useDebouncedCallback(
      (newActivity: TUserMapActivity) => {
        dispatch(setUserMapActivity(newActivity));
        // synchronize map state values
        const mapView = newActivity[recordActivityAsPage as "fleetMap"];
        const newMapMode = gIdentifyAndSetMapModeToStore(mapRef.current.map);
        if (
          !!recordActivityAsPage &&
          !!mapView.center?.latitude &&
          !!mapView.center?.longitude &&
          !!mapView.zoom
        ) {
          dispatch(
            setMapStateData({
              // ...mapState,
              mapCenter: mapRef.current.map
                ? {
                    latitude: mapView.center.latitude,
                    longitude: mapView.center.longitude,
                  }
                : mapState?.mapCenter,
              mapType: mapRef.current.map ? newMapMode : mapState?.mapType,
              mapZoom: mapView.zoom,
              pageMapLoaded: true,
            })
          );
        }
      },
      500
    );

    const initBingMap = () => {
      if (APP_CONFIG.DEBUG.MAPS) console.log("initBingMap");
      const currPosRetrievedCallback = (currPos: TLatLng) => {
        dispatch(setUserCurrPosData(currPos));
        if (mapState?.mapScriptLoaded) {
          const preferredMapType = getUserPreferredMapType();
          // if user has changed map type (specifically in a fleet map session), use that, otherwise use preferred
          const applicableMapType =
            activityRef?.current?.[recordActivityAsPage as "fleetMap"]
              ?.mapType ?? preferredMapType;

          if (APP_CONFIG.DEBUG.MAPS && debugComponent) {
            console.log("basicMap - userMapDefault:", userMapDefault);
            console.log("basicMap - preferredMapType:", preferredMapType);
            console.log("basicMap - applicableMapType:", applicableMapType);
            console.log("basicMap - mapState?.mapType:", mapState?.mapType);
            console.log(
              "basicMap - will use:",
              mapState?.mapType ?? preferredMapType
            );
            console.log("preferredMapType in basicmap.tsx", preferredMapType);
          }

          onMapScriptLoaded({
            mapRef,
            // set to last view if user is on fleet map page - prevent momentary flicker to default location
            mapStateToSetToLastViewOnLoad:
              pathname === routeUrls.dashboardChildren.mapsChildren.fleet
                ? mapState
                : null,
            mapType: applicableMapType,
            mapLayerOptions: getDefaultMapLayerOptions(mapState),
            currentPosition: mapData?.centerPosition ?? currPos,
            recordActivityAsPage,
            nativeMapControls,
            setLoadingMap,
            onMapReadyCallback: () => {
              if (APP_CONFIG.DEBUG.MAPS) console.log("onMapReadyCallback");
              setMapReady(true);
              // set initial map state
              dispatch(
                setMapStateData({
                  // ...mapState,
                  mapType: mapState?.mapType ?? applicableMapType,
                  pageMapLoaded: true,
                })
              );
              // add listener to ensure mapType always reflects the correct map type/mode
              // relying on `tilesloaded` event as this is the last event to be fired
              // reference: https://developers.google.com/maps/documentation/javascript/events
              try {
                // delayed event capture to keep map type in state in sync
                mapRef.current.map.addListener("idle", () => {
                  setTimeout(() => {
                    const newMapMode = gIdentifyAndSetMapModeToStore(
                      mapRef.current.map
                    );
                    if (APP_CONFIG.DEBUG.MAPS) console.log("idle", newMapMode);
                    if (!recordActivityAsPage || refreshingRef.current) return;
                    if (debugComponent) console.log("USERMAPACTIVITY on idle");
                    debouncedSetUserMapActivity({
                      ...activityRef.current,
                      [recordActivityAsPage as "fleetMap"]: {
                        ...activityRef.current?.fleetMap,
                        center: {
                          latitude: mapRef.current.map.getCenter().lat() || 0,
                          longitude: mapRef.current.map.getCenter().lng() || 0,
                        } as TLatLng,
                        zoom: mapRef.current.map.getZoom(),
                        mapType: newMapMode,
                      },
                    } as TUserMapActivity);
                  }, 500);
                });
                mapRef.current.map.addListener("tilesloaded", () => {
                  setTimeout(() => {
                    const newMapMode = gIdentifyAndSetMapModeToStore(
                      mapRef.current.map
                    );
                    if (APP_CONFIG.DEBUG.MAPS)
                      console.log("tilesloaded", newMapMode);
                    // update store with new map mode
                    // disabled, this is being done via `gIdentifyAndSetMapModeToStore`
                    // dispatch(
                    //   setMapStateData({
                    //     ...mapState,
                    //     mapType: newMapMode,
                    //     pageMapLoaded: true,
                    //   } as TMapState)
                    // );
                    // update user map activity in store
                    if (!recordActivityAsPage || refreshingRef.current) return;
                    if (debugComponent)
                      console.log("USERMAPACTIVITY on tilesloaded");
                    debouncedSetUserMapActivity({
                      ...activityRef.current,
                      [recordActivityAsPage as "fleetMap"]: {
                        ...activityRef.current?.fleetMap,
                        center: {
                          latitude: mapRef.current.map.getCenter().lat(),
                          longitude: mapRef.current.map.getCenter().lng(),
                        } as TLatLng,
                        zoom: mapRef.current.map.getZoom(),
                        mapType: newMapMode,
                      },
                    } as TUserMapActivity);
                  }, 600);
                });
                // listen to zoom changed event and update state values
                mapRef.current.map.addListener("zoom_changed", () => {
                  const newMapZoom = gUpdateMapZoomLevelToStore(
                    mapRef.current.map
                  );
                  if (APP_CONFIG.DEBUG.MAPS)
                    console.log("zoom_changed", newMapZoom);
                  // update user map activity in store
                  if (!recordActivityAsPage || refreshingRef.current) return;
                  if (debugComponent)
                    console.log("USERMAPACTIVITY on zoom_changed");
                  debouncedSetUserMapActivity({
                    ...activityRef.current,
                    [recordActivityAsPage as "fleetMap"]: {
                      ...activityRef.current?.fleetMap,
                      center: {
                        latitude: mapRef.current.map.getCenter().lat(),
                        longitude: mapRef.current.map.getCenter().lng(),
                      } as TLatLng,
                      zoom: mapRef.current.map.getZoom(),
                      mapType: gMapGetMode(mapRef.current.map),
                    },
                  } as TUserMapActivity);
                });
                // listen to center changed event and update state values
                mapRef.current.map.addListener("center_changed", () => {
                  if (APP_CONFIG.DEBUG.MAPS) console.log("center_changed");
                  // update user map activity in store
                  if (!recordActivityAsPage || refreshingRef.current) return;
                  if (debugComponent)
                    console.log("USERMAPACTIVITY on center_changed");
                  debouncedSetUserMapActivity({
                    ...activityRef.current,
                    [recordActivityAsPage as "fleetMap"]: {
                      ...activityRef.current?.fleetMap,
                      center: {
                        latitude: mapRef.current.map.getCenter().lat(),
                        longitude: mapRef.current.map.getCenter().lng(),
                      } as TLatLng,
                      zoom: mapRef.current.map.getZoom(),
                      mapType: gMapGetMode(mapRef.current.map),
                    },
                  } as TUserMapActivity);
                });
              } catch (e) {
                if (APP_CONFIG.DEBUG.ALL)
                  console.error(
                    "An error occurred while initializing the map:",
                    e
                  );
              }
              onMapReady?.();
            },
          });
        } else {
          // TODO: handle error when user lands directly on a page having map
          if (APP_CONFIG.DEBUG.MAPS)
            console.log("ERROR: Map script should have been loaded by now");
        }
      };

      // 7/3/2024: we let the map load directly and center to default location, as we currently don't need user's current position
      currPosRetrievedCallback(APP_CONFIG.MAPS.DEFAULTS.CENTER);

      // disabled 7/3/2024, as we currently don't need user's current position
      // get user's current position, use if available
      // if(userCurrPos) {
      //   // console.log('[basicMap] APP HAS USER CURR POS')
      //   currPosRetrievedCallback(userCurrPos);
      // } else {
      //   console.log('[basicMap] APP DOES NOT HAVE USER CURR POS YET')
      //   mapGetCurrentPosition(currPosRetrievedCallback);
      // }
    };

    const MemoizedGoogleMapsReact = useCallback(() => {
      // if(APP_CONFIG.DEBUG.MAPS) console.log('MemoizedGoogleMapsReact')

      // initialize the map
      if (!initRef.current && mapState?.mapScriptLoaded) {
        initRef.current = true;
        setTimeout(() => initBingMap(), 500);
      }

      return <></>;
    }, [initBingMap, mapState]);

    return (
      <div className={`relative ${className}`}>
        {loadingMap && <MapLoadingAnimation />}
        <div
          id={APP_CONFIG.MAPS.COMPONENT_ID}
          className="w-full h-full relative"
        ></div>
        {/* <>{console.log('[[ RERENDER ]]')}</> */}
        <MemoizedGoogleMapsReact />
      </div>
    );
  }
);
export default BasicMap;
